<script>
import {
  formButton,
  formInput,
  formPhone,
  isValidCPF,
  normalizeDocument
} from 'tb-design_system-web'
import { useForm } from 'vee-validate'
import { ref, provide, onMounted, watch } from 'vue'
import { useMeta } from 'vue-meta'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import * as yup from 'yup'

import RegisterLayout from '@/components/shared/registerLayout.vue'
import StepProgressBar from '@/components/shared/stepProgressBar.vue'
import { validateStepAccess } from '@/composables/utils/onboardingGuards'
import { isValidIntlPhone } from '@/composables/utils/phoneGoogle'
import { validateClientId, validateExistingEmail } from '@/services/api'
import { handleGlobalError } from '@/services/errorHandler'
import { checkCompliance } from '@/services/ms'
import { useOnboardingStore } from '@/stores/onboarding'

export default {
  name: 'RegisterPFStep1',
  components: {
    RegisterLayout,
    StepProgressBar,
    formInput,
    formButton,
    formPhone
  },

  setup() {
    useMeta({ title: 'Cadastro PF - Etapa 1' })

    const router = useRouter()
    const onboardingStore = useOnboardingStore()

    onMounted(() => {
      validateStepAccess(onboardingStore, 'PF', [], router)
    })

    const step1Data = onboardingStore.getStepData(1) || {}

    const customerValue = ref(step1Data.customer || '')
    const nameValue = ref(step1Data.name || '')
    const emailValue = ref(step1Data.email || '')
    const phoneValue = ref(step1Data.phone || '')

    const loading = ref(false)

    const isDead = ref(false)
    const isDocumentValid = ref(false)
    const isResponseCompliance = ref(false)

    const customerNotFound = ref(false)

    const emailExists = ref(false)
    const emailExistsMessage = ref('')

    const phoneValidate = ref(null)

    const alreadySubmittedOnce = ref(false)
    provide('alreadySubmittedOnce', alreadySubmittedOnce)

    const customerSchema = yup.object({
      customer: yup
        .string()
        .required()
        .test((value) => isValidCPF(value))
    })

    const { handleSubmit, setFieldError } = useForm({
      validationSchema: yup.object({
        customer: yup
          .string()
          .required('CPF é obrigatório')
          .test('cpf', 'CPF inválido', (value) => isValidCPF(value)),
        name: yup
          .string()
          .required('Nome é obrigatório')
          .min(6, 'Mínimo de 6 caracteres')
          .max(100, 'Máximo de 100 caracteres')
          .test(
            'full-name',
            'Informe pelo menos nome e sobrenome, com 2 letras ou mais cada',
            (value) => {
              if (!value) return false
              const words = value.trim().split(/\s+/)
              return words.length >= 2 && words.filter((word) => word.length >= 2).length >= 2
            }
          ),
        email: yup.string().required('E-mail é obrigatório').email('E-mail inválido'),
        phone: yup
          .string()
          .required('Telefone é obrigatório')
          .test('is-valid-phone', 'Telefone inválido', (value) => isValidIntlPhone(value))
      })
    })

    const onSubmitAction = () => {
      alreadySubmittedOnce.value = true
      return onSubmit()
    }

    const onSubmit = handleSubmit(async () => {
      loading.value = true
      emailExists.value = false
      emailExistsMessage.value = ''

      try {
        const cpfExists = await validateCpfExists()
        if (cpfExists) {
          loading.value = false
          return
        }

        const emailDuplicated = await validateEmailExists()
        if (emailDuplicated) {
          loading.value = false
          return
        }

        onboardingStore.setStepData(1, {
          customer: normalizeDocument(customerValue.value),
          name: nameValue.value,
          email: emailValue.value,
          phone: phoneValue.value
        })

        router.push('/register/pf/step-2')
      } catch (err) {
        handleGlobalError(err)
      } finally {
        loading.value = false
      }
    })

    const validateCpfExists = async () => {
      const cleanCpf = normalizeDocument(customerValue.value)

      try {
        const clientRes = await validateClientId(cleanCpf)
        if (clientRes?.data?.payload?.exists === true) {
          setFieldError('customer', 'CPF já cadastrado')
          toast('CPF já cadastrado.', { type: 'error', autoClose: 3000 })
          return true
        }
      } catch (err) {
        handleGlobalError(err, 'Erro ao verificar CPF:')
      }

      return false
    }

    const validateEmailExists = async () => {
      try {
        const emailRes = await validateExistingEmail(emailValue.value.trim())
        if (emailRes?.data?.payload?.exists === true) {
          setFieldError('email', 'E-mail já cadastrado')
          emailExists.value = true
          emailExistsMessage.value = 'E-mail já cadastrado'
          toast('E-mail já cadastrado.', { type: 'error', autoClose: 3000 })
          return true
        }
      } catch (err) {
        handleGlobalError(err, 'Erro ao verificar e-mail:')
      }

      return false
    }

    watch(customerValue, (customerSearch) => {
      if (customerSearch.length > 2) doActionSubmit()
      if (customerSearch.length === 0) cleanRegister()
    })

    const cleanRegister = async () => {
      isResponseCompliance.value = false

      customerValue.value = ''
      nameValue.value = ''
      emailValue.value = ''
      phoneValue.value = ''
    }

    const doActionSubmit = () => {
      alreadySubmittedOnce.value = true
      customerNotFound.value = false
      return doCheckCustomer()
    }

    const doCheckCustomer = async () => {
      const isValid = await customerSchema.isValid({ customer: customerValue.value })
      if (!isValid) return
      try {
        loading.value = true
        let responseExtractCompliance = ''
        const cleanCustomer = normalizeDocument(customerValue.value)
        const response = await checkCompliance('person', { cpf: cleanCustomer })
        responseExtractCompliance = response.data.payload

        isDead.value = responseExtractCompliance.isDead
        isDocumentValid.value = responseExtractCompliance.isValid

        const isProd = import.meta.env.VITE_APP_ENVIRONMENT === 'prod'

        if (!isDocumentValid.value && isProd) {
          toast(`CPF inválido!`, {
            autoClose: 3000,
            theme: 'auto',
            type: 'error'
          })
          customerNotFound.value = true
          return
        }

        if (isDead.value && isProd) {
          toast(`Infelizmente seu cadastro não foi aprovado!`, {
            autoClose: 3000,
            theme: 'auto',
            type: 'warning'
          })
          customerNotFound.value = true
          return
        }

        isResponseCompliance.value = true
        nameValue.value = responseExtractCompliance.name
      } catch (e) {
        if (e.response?.data?.status === 404) {
          toast(`CPF não encontrado!`, {
            autoClose: 3000,
            theme: 'auto',
            type: 'error'
          })
          customerNotFound.value = true
          return
        } else if (e.response?.data?.type?.includes('under_18')) {
          toast(`Infelizmente seu cadastro não foi aprovado!`, {
            autoClose: 3000,
            theme: 'auto',
            type: 'warning'
          })
          customerNotFound.value = true
          return
        }
        handleGlobalError(e, 'ERR_COMPLIANCE:')
      } finally {
        loading.value = false
      }
    }

    return {
      customerValue,
      customerNotFound,
      nameValue,
      emailValue,
      phoneValue,
      loading,
      onSubmitAction,
      phoneValidate
    }
  }
}
</script>

<template>
  <RegisterLayout>
    <div class="col-12 col-md-9 h-100 onboarding pb-5">
      <div class="row">
        <StepProgressBar class="mt-5 mb-5" :steps="['Dados', 'Demanda', 'Senha']" :current="1" />
      </div>
      <div class="row d-flex justify-content-center mt-4">
        <div class="col-12 col-md-6 col-lg-5 d-flex flex-column align-items-center">
          <form-input
            class="col-12 form-input mb-3"
            label="CPF"
            v-model="customerValue"
            id="customer"
            name="customer"
            type="tel"
            :is-invalid="customerNotFound"
            :isDisabled="loading"
          />
          <form-input
            class="col-12 form-input mb-3"
            label="Nome completo"
            v-model="nameValue"
            id="name"
            name="name"
            type="name"
          />
          <form-input
            class="col-12 form-input mb-3"
            label="E-mail"
            v-model="emailValue"
            id="email"
            name="email"
            type="email"
          />
          <form-phone
            class="col-12 form-input"
            id="phone"
            name="phone"
            label="DDD + Telefone"
            v-model="phoneValue"
          />
          <form-button
            class="col-12 form-input"
            title="Próximo"
            :loading="loading"
            :isDisabled="loading || customerNotFound"
            @clicked="onSubmitAction"
          />
        </div>
      </div>
    </div>
  </RegisterLayout>
</template>

<style lang="scss" scoped></style>
